import Cookie from '@wetransfer/concorde-cookie';
import cloneDeep from 'lodash/cloneDeep';

import { actions } from '@app/action/transfer';
import Helper from '@app/helper';

import { convertExpiry } from '@app/transfer-bundle/helpers/expiry';
import Transfer from '@module/transfer';

import { isLoggedInFreeUser, isLoggedInPlusUser } from '@app/helper/user';
import { isSendingToDefaultRecipientSelector } from '@app/selector/transfers';
import { TRANSFER_STATES } from '@app/transfer-bundle/constant/transfer-states';
import { TRANSFER_TYPES } from '@app/transfer-bundle/constant/transfer-types';
import { normalizeTransfer } from '@app/transfer-bundle/helpers/normalize-transfer';
import TransferItem from '@app/transfer-bundle/models/transfer-item';
import {
  EXPERIMENT_EMAIL_TRANSFERS,
  EXPERIMENT_EMAIL_TRANSFERS_DEFAULT_LINK,
  EXPERIMENT_EMAIL_TRANSFERS_NO_EMAIL,
} from '@app/constant/experiments';
import LaunchDarkly from '@app/modules/launchdarkly';
import { AppConfig } from '@app/core-bundle/config/app-config';
import {
  COOKIE_TRANSFER_TYPE_LINK,
  COOKIE_TRANSFER_TYPE_EMAIL,
} from '@app/constant/cookie-transfer-types';

function getDefaultRecipient(user, account, channel) {
  const channelName = channel.name || '';
  const channelEmail = channel.defaultRecipient || false;

  // User is logged in but not on it's own channel and there is a channel email available
  if (user.loggedIn && channelName !== account.subdomain_name && channelEmail) {
    return channelEmail;
  }
  // User is not logged in but a channel email is available
  if (!user.loggedIn && channelEmail) {
    return channelEmail;
  }

  return false;
}

const checkExpiry = (user) =>
  user &&
  user.preferences &&
  user.preferences.transfer_default_expiry !== undefined;

const getUserType = ({ user, channel, transferFromState }) => {
  if (isLoggedInPlusUser(user)) {
    return 'plus';
  }

  if (
    isSendingToDefaultRecipientSelector({
      transfer: transferFromState,
      channel,
    })
  ) {
    return 'plus';
  }

  if (isLoggedInFreeUser(user)) {
    return 'freeAccount';
  }

  return 'free';
};

const createTransfer = ({ user, channel, account, transferFromState }) => {
  // Get the last used sender from a cookie
  const defaultSender = Helper.getLastUsedSender();
  const defaultRecipient = getDefaultRecipient(user, account, channel);

  const experimentEmailTransfersVariant = LaunchDarkly.getVariant(
    EXPERIMENT_EMAIL_TRANSFERS
  );

  // Get the last used type from a cookie
  let defaultType = Cookie.get(AppConfig.cookies.transferType.name);
  if (defaultType === COOKIE_TRANSFER_TYPE_LINK) {
    defaultType = TRANSFER_TYPES.LINK;
  }
  if (defaultType === '3') {
    defaultType = TRANSFER_TYPES.EMAIL;
  }
  if (defaultType === '2') {
    defaultType = TRANSFER_TYPES.EMAIL;
  }
  if (defaultType === COOKIE_TRANSFER_TYPE_EMAIL) {
    defaultType = TRANSFER_TYPES.EMAIL;
  }
  if (experimentEmailTransfersVariant === EXPERIMENT_EMAIL_TRANSFERS_NO_EMAIL) {
    defaultType = TRANSFER_TYPES.LINK;
  }

  // For logged out users who are first time in the email transfers experiment, determined by the
  // emailTransfersExperiment cookie, we force the default to be link transfers. If they then
  // decide to change it during the course of the experiment, we are going to respect their choice.
  if (
    !user.loggedIn &&
    !Cookie.get(AppConfig.cookies.emailTransfersExperiment.name) &&
    (experimentEmailTransfersVariant ===
      EXPERIMENT_EMAIL_TRANSFERS_DEFAULT_LINK ||
      experimentEmailTransfersVariant === EXPERIMENT_EMAIL_TRANSFERS_NO_EMAIL)
  ) {
    Cookie.set(AppConfig.cookies.emailTransfersExperiment.name, 1);
    Cookie.set(AppConfig.cookies.transferType.name, COOKIE_TRANSFER_TYPE_LINK, {
      secure: !__DEV__,
      days: 365,
    });
    defaultType = TRANSFER_TYPES.LINK;
  }

  // We only want to allow email transfers when the default recipient is locked in profile pages
  if (
    Helper.isDefaultRecipientInUploadLocked({
      channelDefaultRecipient: channel.defaultRecipient,
      channelProfileReceivingLocked: channel.profileReceivingLocked,
      userLoggedIn: user.loggedIn,
      channelName: channel.name,
      accountSubDomainName: account.subdomain_name,
    })
  ) {
    defaultType = TRANSFER_TYPES.EMAIL;
  }

  // Create a new Transfer object
  const newTransfer = new Transfer({
    userType: getUserType({ user, channel, transferFromState }),
    type:
      defaultType === TRANSFER_TYPES.LINK
        ? TRANSFER_TYPES.LINK
        : TRANSFER_TYPES.EMAIL,
    onChannel: !!channel.name,
    accountId: account.id,
  });

  // Should we fill in the sender?
  if (defaultSender) {
    newTransfer.sender = defaultSender;
  }

  // Should we add a default recipient?
  if (defaultRecipient) {
    newTransfer.recipients.push(defaultRecipient);
  }

  // Assign channel name to transfer
  if (channel.name) {
    newTransfer.channel = channel.name;
  }

  // If there is a default expiry available, please use it.
  if (checkExpiry(user)) {
    newTransfer.expiry = convertExpiry(
      user.preferences.transfer_default_expiry
    );
  }

  return cloneDeep(newTransfer);
};

const similarTransfer = ({
  transferToDuplicate,
  transferFromState,
  account,
  channel,
  user,
}) => {
  const newTransfer = new Transfer({
    userType: getUserType({ user, channel, transferFromState }),
    type: transferToDuplicate.type,
    accountId: account.id,
  });

  if (transferToDuplicate.sender) {
    newTransfer.sender = transferToDuplicate.sender;
  }

  if (transferToDuplicate.channel) {
    newTransfer.channel = transferToDuplicate.channel;
  }

  if (transferToDuplicate.expiry) {
    newTransfer.expiry = transferToDuplicate.expiry;
  }

  if (
    Helper.isDefaultRecipientInUploadLocked({
      channelDefaultRecipient: channel.defaultRecipient,
      channelProfileReceivingLocked: channel.profileReceivingLocked,
      userLoggedIn: user.loggedIn,
      channelName: channel.name,
      accountSubDomainName: account.subdomain_name,
    })
  ) {
    newTransfer.recipients = transferToDuplicate.recipients;
  }

  return cloneDeep(newTransfer);
};

const handleError = (transfer, error) => {
  if (
    ['LimitReached', 'UserBlocked', 'UnavailableParent'].indexOf(error) !== -1
  ) {
    transfer.lastError = error;
  }

  return transfer;
};

const forwardTransfer = ({
  transferToForward,
  transferFromState,
  account,
  user,
  channel,
}) => {
  // A forward is kind of a similar transfer
  const forward = similarTransfer({
    transferToDuplicate: transferToForward,
    transferFromState,
    account,
    channel,
    user,
  });

  // Set our parent id and message
  forward.parent_id = transferToForward.id;
  forward.message = transferToForward.message || '';

  // Is there an account id?
  if (account.id) {
    forward.accountId = account.id;
  }

  // If it's a link we'll just switch back to e-mail
  // and use last sender from cookie.
  if (forward.type === TRANSFER_TYPES.LINK) {
    forward.sender = Helper.getLastUsedSender();
    forward.type = TRANSFER_TYPES.EMAIL;
  }

  // Sometimes we still haven't filled in a sender
  // even though we DO have one. Let's use that!
  if (transferFromState.sender && !forward.sender) {
    forward.sender = transferFromState.sender;
  }

  if (
    Helper.isDefaultRecipientInUploadLocked({
      channelDefaultRecipient: channel.defaultRecipient,
      channelProfileReceivingLocked: channel.profileReceivingLocked,
      userLoggedIn: user.loggedIn,
      channelName: channel.name,
      accountSubDomainName: account.subdomain_name,
    })
  ) {
    forward.recipients = transferFromState.recipients;
  }

  // If there is a default expiry available, please use it.
  if (checkExpiry(user)) {
    forward.expiry = convertExpiry(user.preferences.transfer_default_expiry);
  }

  return forward;
};

const findFile = (file_id) => (file) => file.id === file_id;

export default function(state = {}, action) {
  const { type } = action;
  switch (type) {
    // this should replace all the old "new Transfer" functionality
    // so transfer lives 100% in the state
    case actions.TRANSFER_INIT: {
      const transfer = new Transfer({
        userType: action.userType,
        type: action.transferType,
        ...(action.accountId ? { accountId: action.accountId } : {}),
        ...(action.spaceId ? { spaceId: action.spaceId } : {}),
      });

      transfer.onChannel = !!action.channelName;
      if (action.channelName) {
        transfer.channel = action.channelName;
      }

      return transfer;
    }

    case actions.TRANSFER_PREPARE_DOWNLOAD: {
      let transferDetails = { ...action.params };
      transferDetails.items = (transferDetails.items || []).map(
        ({ file, item_type, ...item }) => ({
          ...item,
          file: file || new TransferItem(item),
          apiType: item_type,
        })
      );

      const transfer = new Transfer();

      transferDetails = normalizeTransfer(transferDetails);
      if (transferDetails.items) {
        transfer.items = transferDetails.items;
      }
      transfer.itemsTree = transferDetails.itemsTree;
      transfer.title = transferDetails.title;

      if (transferDetails.id) {
        transfer.id = transferDetails.id;
      }
      if (transferDetails.shortened_url) {
        transfer.shortURL = transferDetails.shortened_url;
      }
      if (transferDetails.security_hash) {
        transfer.secret = transferDetails.security_hash;
      }
      if (transferDetails.recipient_id) {
        transfer.recipient_id = transferDetails.recipient_id;
      }
      if (transferDetails.state) {
        transfer.state = transferDetails.state;
      }
      if (transferDetails.message) {
        transfer.message = transferDetails.message;
      }
      if (transferDetails.account_id) {
        transfer.accountId = transferDetails.account_id;
      }

      if ('description' in transferDetails) {
        transfer.message = transferDetails.description;
      }
      if ('expiry_in_seconds' in transferDetails) {
        transfer.expiry_seconds = transferDetails.expiry_in_seconds;
      }

      // If we are password protected and don't know the password, change the state
      if (
        (transfer.state === TRANSFER_STATES.PROCESSING ||
          transfer.state === TRANSFER_STATES.DOWNLOADABLE) &&
        transferDetails.password_protected === true &&
        !action.password
      ) {
        transfer.state = TRANSFER_STATES.PASSWORD_PROTECTED;
      }

      // If we are password protected and have the password, remember if for later.
      if (transferDetails.password_protected && action.password) {
        transfer.password = action.password;
      }

      return transfer;
    }

    case actions.TRANSFER_CREATE:
      return createTransfer({
        user: action.user,
        channel: action.channel,
        account: action.account,
        transferFromState: action.transfer,
      });

    case actions.TRANSFER_CREATE_SIMILAR:
      // In the case of the TRANSFER_CREATE_SIMILAR action the transferToDuplicate
      // and the transferFromState correspond to the same transfer.
      return similarTransfer({
        transferToDuplicate: action.transfer,
        transferFromState: action.transfer,
        account: action.account,
        channel: action.channel,
        user: action.user,
      });

    case actions.TRANSFER_CREATE_FORWARD:
      return forwardTransfer({
        transferToForward: action.transferToForward,
        transferFromState: action.transferFromState,
        account: action.account,
        user: action.user,
        channel: action.channel,
      });

    case actions.TRANSFER_CHANGE_TYPE:
      state.type = action.transferType;
      return cloneDeep(state);

    case actions.TRANSFER_CHANGE_EXPIRY:
      state.expiry = action.period;
      return cloneDeep(state);

    case actions.TRANSFER_CHANGE_PASSWORD:
      state.password = action.password;
      return cloneDeep(state);

    case actions.TRANSFER_SET_SENDER:
      state.sender = action.email;
      return cloneDeep(state);

    case actions.TRANSFER_SET_MESSAGE:
      state.message = action.message;
      return cloneDeep(state);

    case actions.TRANSFER_ADD_RECIPIENT:
      for (const email of action.emails) {
        if (state.recipients.indexOf(email) === -1) {
          state.recipients.push(email);
        }
      }
      return cloneDeep(state);

    case actions.TRANSFER_ADD_FILES: {
      // We use the transfer normalize to handle the
      // items/files/fileTree building, but omit other
      // properties to prevent clashing
      const { files, items, itemsTree } = normalizeTransfer({
        ...state,
        files: [...state.files, ...action.files],
      });

      return {
        ...state,
        files,
        items,
        itemsTree,
      };
    }

    case actions.TRANSFER_REMOVE_FILE: {
      // We use the transfer normalize to handle the
      // items/files/fileTree building, but omit other
      // properties to prevent clashing
      const { files, items, itemsTree } = normalizeTransfer({
        ...state,
        files: state.files.filter((file) => file.name !== action.fileName),
      });

      return {
        ...state,
        files,
        items,
        itemsTree,
      };
    }

    case actions.TRANSFER_REMOVE_DIRECTORY: {
      const directoryName = action.directoryName;
      const path = `${directoryName}/`;

      // We use the transfer normalize to handle the
      // items/files/fileTree building, but omit other
      // properties to prevent clashing
      const { files, items, itemsTree } = normalizeTransfer({
        ...state,
        files: state.files.filter(
          (file) => file.name !== directoryName && !file.name.startsWith(path)
        ),
      });

      return {
        ...state,
        files,
        items,
        itemsTree,
      };
    }

    case actions.TRANSFER_REMOVE_RECIPIENT:
      state.recipients = state.recipients.filter(
        (recipient) => recipient !== action.email
      );
      return cloneDeep(state);

    case actions.TRANSFER_RESET:
      state.id = false;
      state.state = TRANSFER_STATES.INIT;
      return state;

    case actions.TRANSFER_START:
      return state;

    case actions.TRANSFER_STATE_DOWNLOADABLE:
      state.state = TRANSFER_STATES.DOWNLOADABLE;
      return state;

    case actions.TRANSFER_STATE_DOWNLOAD_STARTING:
      state.state = TRANSFER_STATES.DOWNLOADING;
      return state;

    case actions.TRANSFER_STATE_FINISHED:
      return { ...state, state: TRANSFER_STATES.FINISHED };

    case actions.RECAPTCHA_CHALLENGE_RECEIVED:
      state.id = action.transfer_id;
      state.state = TRANSFER_STATES.RECAPTCHA;
      return state;

    case actions.UPLOAD_ERROR:
      return handleError(state, action.error);

    case actions.TRANSFER_STATE_DOWNLOAD_STARTING_FILE:
      state.items.find(
        (item) => item.id === action.file_id
      ).file.state.download = 'downloading';
      return state;

    case actions.TRANSFER_STATE_FINISHED_FILE:
      state.items.find(
        (item) => item.id === action.file_id
      ).file.state.download = 'done';
      return state;

    case actions.TRANSFER_RESET_DOWNLOAD:
      state.state = TRANSFER_STATES.DOWNLOADABLE;
      return state;

    case actions.TRANSFER_DOWNLOAD_INVALID:
      state.state = TRANSFER_STATES.INVALID;
      return state;

    case actions.TRANSFER_DOWNLOAD_EXPIRED:
      state.state = TRANSFER_STATES.EXPIRED;
      return state;

    case actions.PREVIEW_LOADING: {
      const itemIndex = state.items.findIndex(findFile(action.file_id));
      const item = state.items[itemIndex];

      item.file.state.error = false;
      item.file.state.loaded = false;
      item.file.previewUri = action.file_url;

      return state;
    }

    case actions.PREVIEW_LOADED: {
      const itemIndex = state.items.findIndex(findFile(action.file_id));
      const item = state.items[itemIndex];

      item.file.state.error = false;
      item.file.state.loaded = true;

      return state;
    }

    case actions.UPLOAD_NEED_VERIFICATION: {
      state.verification_required = true;
      return state;
    }

    case actions.UPLOAD_VERIFIED: {
      state.verification_required = false;
      return state;
    }

    case actions.PREVIEW_ERROR: {
      const itemIndex = state.items.findIndex(findFile(action.file_id));
      const item = state.items[itemIndex];

      item.file.state.error = true;
      item.file.state.loaded = false;
      item.file.previewUri = null;

      return state;
    }

    case actions.TRANSFER_SET_LANGUAGE:
      return { ...state, language: action.language };

    case actions.TRANSFER_SET_DOMAIN_USER_ID:
      return { ...state, domainUserId: action.domainUserId };

    case actions.TRANSFER_SET_CHANNEL:
      return { ...state, channel: action.channel };

    case actions.TRANSFER_SET_STATE:
      return { ...state, state: action.state };

    case actions.TRANSFER_SET_EXPIRY_SECONDS: {
      if (typeof action.expiryInSeconds !== 'number') {
        return state;
      }
      return { ...state, expiry_seconds: action.expiryInSeconds };
    }

    case actions.TRANSFER_SET_SHORT_URL:
      return { ...state, shortURL: action.shortURL };

    case actions.TRANSFER_SET_ID:
      return { ...state, id: action.id };

    case actions.TRANSFER_SET_DISPLAY_NAME:
      return { ...state, displayName: action.displayName };

    case actions.TRANSFER_SET_SECURITY_HASH:
      return { ...state, securityHash: action.securityHash };

    case actions.TRANSFER_SET_UPLOAD_STARTED_AT:
      return { ...state, uploadStartedAt: action.uploadStartedAt };
  }

  return state;
}
